// Egress guards for the per-store cloud loads that run on login/boot.
//
// Problem this solves: App.tsx fires ~20 store initialize()s when `user`
// resolves, and each store's "already loaded" gate (`initializedUserId`)
// is only set AFTER an awaited auth check + fetch — so the double effect
// runs in dev StrictMode (and the `user` identity churn from
// checkAuth/onAuthStateChange in prod) all race past the gate and every
// table gets fetched several times per page load. On top of that, every
// reload re-downloads every table even though the zustand caches in
// localStorage are seconds old. Both patterns multiply Supabase egress.
//
// Two independent guards:
//
// 1. dedupeInit(key, fn) — collapses CONCURRENT initialize() calls into one
//    in-flight promise per store. The entry is dropped when it settles, so a
//    failed load can be retried by a later call; after a successful load the
//    store's own `initializedUserId` gate makes repeats no-ops.
//    ⚠️⚠️ PER ACCOUNT. A sign-out does not reload the page: the next account's
//    initialize() JOINED the last account's load still on its way — its own load
//    never ran, and the store took the last account's data. The in-flight map is
//    keyed by the account's generation (lib/accountStorage), which moves on every
//    change of account: a load asked for before a change never stands in for one
//    asked for after it — not even for the same account signing back in, since a
//    store drops an answer that lands after the change.
//
// 2. isCloudFresh / markCloudFresh — a localStorage timestamp per
//    store+user, stamped after each successful cloud load. While the stamp
//    is younger than the TTL, initialize() can boot purely from the
//    persisted local cache (the caller MUST also verify the cache belongs
//    to this user via its `cachedUserId` before skipping). Write-through is
//    untouched — mutations still hit the cloud immediately — so the local
//    cache stays canonical for this device and a warm reload needs no reads.

import { accountGeneration } from './accountStorage';

const inflight = new Map<string, Promise<void>>();

export function dedupeInit(key: string, fn: () => Promise<void>): Promise<void> {
  const slot = `${key}@${accountGeneration()}`;
  const existing = inflight.get(slot);
  if (existing) return existing;
  const p = fn().finally(() => {
    inflight.delete(slot);
  });
  inflight.set(slot, p);
  return p;
}

const FRESH_KEY = 'metanoia-cloud-fresh';

// How long a successful cloud load keeps reloads read-free. Short enough
// that cross-device edits still appear on the next coffee-break reload,
// long enough to absorb reload/navigation storms and app cold-starts.
export const CLOUD_FRESH_TTL_MS = 5 * 60 * 1000;

// The store test suites assert that initialize() loads from (mocked)
// supabase; a freshness stamp leaking between tests in one jsdom would
// silently skip those loads. The global test setup disables the freshness
// gate; cloudBoot's own unit tests re-enable it locally.
let freshnessEnabled = true;
export function __setCloudFreshnessEnabled(on: boolean): void {
  freshnessEnabled = on;
}

function readMarks(): Record<string, number> {
  try {
    return JSON.parse(localStorage.getItem(FRESH_KEY) ?? '{}') as Record<string, number>;
  } catch {
    return {};
  }
}

export function isCloudFresh(
  store: string,
  userId: string,
  ttlMs: number = CLOUD_FRESH_TTL_MS
): boolean {
  if (!freshnessEnabled) return false;
  const at = readMarks()[`${store}:${userId}`];
  return typeof at === 'number' && Date.now() - at < ttlMs;
}

export function markCloudFresh(store: string, userId: string): void {
  if (!freshnessEnabled) return;
  try {
    const marks = readMarks();
    marks[`${store}:${userId}`] = Date.now();
    localStorage.setItem(FRESH_KEY, JSON.stringify(marks));
  } catch {
    // localStorage unavailable/full — freshness is an optimization only.
  }
}
